import React from 'react';
import { InfoIcon } from 'lucide-react';
import { formatTokenAmount, formatPercentage } from '../utils/helpers';

const SwapPreview = ({ 
  fromToken, 
  toToken, 
  fromAmount, 
  estimatedOutput, 
  feeAmount, 
  slippage,
  priceImpact,
  isLoading 
}) => {
  
  if (!fromToken || !toToken || !fromAmount || fromAmount === '0') {
    return null;
  }

  const netOutput = estimatedOutput ? 
    (parseFloat(estimatedOutput) - parseFloat(feeAmount || 0)).toString() : '0';

  return (
    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 space-y-3">
      <div className="flex items-center space-x-2 mb-3">
        <InfoIcon className="w-4 h-4 text-blue-400" />
        <span className="text-sm font-medium text-white">Swap Preview</span>
      </div>

      {/* Exchange Rate */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-white/60">Exchange Rate</span>
        <div className="text-right">
          {isLoading ? (
            <div className="animate-pulse bg-white/10 h-4 w-24 rounded"></div>
          ) : estimatedOutput ? (
            <span className="text-white">
              1 {fromToken.symbol} = {
                formatTokenAmount(
                  (parseFloat(estimatedOutput) / parseFloat(fromAmount)).toString(),
                  0,
                  6
                )
              } {toToken.symbol}
            </span>
          ) : (
            <span className="text-white/40">-</span>
          )}
        </div>
      </div>

      {/* Expected Output */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-white/60">Expected Output</span>
        <div className="text-right">
          {isLoading ? (
            <div className="animate-pulse bg-white/10 h-4 w-20 rounded"></div>
          ) : estimatedOutput ? (
            <span className="text-white">
              {formatTokenAmount(estimatedOutput, toToken.decimals)} {toToken.symbol}
            </span>
          ) : (
            <span className="text-white/40">-</span>
          )}
        </div>
      </div>

      {/* Fee Information */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-white/60">Platform Fee (1%)</span>
        <div className="text-right">
          {isLoading ? (
            <div className="animate-pulse bg-white/10 h-4 w-16 rounded"></div>
          ) : feeAmount && parseFloat(feeAmount) > 0 ? (
            <span className="text-orange-300">
              {formatTokenAmount(feeAmount, toToken.decimals)} {toToken.symbol}
            </span>
          ) : (
            <span className="text-white/40">-</span>
          )}
        </div>
      </div>

      {/* Net Amount After Fee */}
      <div className="flex items-center justify-between text-sm border-t border-white/10 pt-3">
        <span className="text-white/80 font-medium">You Receive</span>
        <div className="text-right">
          {isLoading ? (
            <div className="animate-pulse bg-white/10 h-4 w-20 rounded"></div>
          ) : netOutput && parseFloat(netOutput) > 0 ? (
            <span className="text-green-300 font-medium">
              {formatTokenAmount(netOutput, toToken.decimals)} {toToken.symbol}
            </span>
          ) : (
            <span className="text-white/40">-</span>
          )}
        </div>
      </div>

      {/* Minimum Received (with slippage) */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-white/60">Minimum Received</span>
        <div className="text-right">
          {isLoading ? (
            <div className="animate-pulse bg-white/10 h-4 w-20 rounded"></div>
          ) : netOutput && parseFloat(netOutput) > 0 ? (
            <span className="text-white">
              {formatTokenAmount(
                (parseFloat(netOutput) * (1 - slippage / 100)).toString(),
                toToken.decimals
              )} {toToken.symbol}
            </span>
          ) : (
            <span className="text-white/40">-</span>
          )}
        </div>
      </div>

      {/* Slippage Tolerance */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-white/60">Slippage Tolerance</span>
        <span className="text-white">{formatPercentage(slippage)}</span>
      </div>

      {/* Price Impact Warning */}
      {priceImpact && parseFloat(priceImpact) > 3 && (
        <div className="bg-yellow-500/10 border border-yellow-400/20 rounded-lg p-2 mt-3">
          <div className="flex items-center space-x-2">
            <InfoIcon className="w-4 h-4 text-yellow-400" />
            <span className="text-yellow-300 text-sm">
              High price impact: {formatPercentage(priceImpact)}
            </span>
          </div>
        </div>
      )}

      {/* Slippage Warnings */}
      {slippage > 5 && (
        <div className="bg-red-500/10 border border-red-400/20 rounded-lg p-2 mt-3">
          <div className="flex items-center space-x-2">
            <InfoIcon className="w-4 h-4 text-red-400" />
            <span className="text-red-300 text-sm">
              Very high slippage tolerance may result in unfavorable trades
            </span>
          </div>
        </div>
      )}
      {slippage > 2 && slippage <= 5 && (
        <div className="bg-yellow-500/10 border border-yellow-400/20 rounded-lg p-2 mt-3">
          <div className="flex items-center space-x-2">
            <InfoIcon className="w-4 h-4 text-yellow-400" />
            <span className="text-yellow-300 text-sm">
              High slippage tolerance - consider reducing for better prices
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SwapPreview;